/**
 * The in-game progress column on the RIGHT EDGE: a solar system that fills in as the player scores.
 *
 * It used to be a stack of stars, and that read as the same thing as the journey stars on the
 * progress bar — two different quantities wearing one symbol, which confused players. Planets are
 * unmistakably their own ladder: Mercury first, the Sun last, and nothing else in the game uses
 * them.
 *
 * The body in flight fills from the bottom (see setProgress), so the column moves on every correct
 * answer rather than jumping a whole slot at a time, and an electric line marks the level it has
 * reached (see drawSpark) — without it a body that has barely started shows nothing at all. Both
 * run from the body's lowest point to its highest (see fillHeight), so the level never strays into
 * the gap between two planets. Past the Sun the run keeps going: each extra step brightens it and
 * adds another flare, so an exceptional player still sees something happen — and the line stays on
 * the Sun, climbing it again per level, so the answers between those steps still move something.
 *
 * Pure Phaser Graphics — no image assets, like src/game/planet.ts.
 */
import Phaser from 'phaser';
import { FONT_STACK } from '@/game/config';
import { ECLIPSE_STARS } from '@/game/milestones';

/**
 * Slot order, innermost first: all eight planets, Mercury out to Neptune, with the Sun as the
 * prize on the ninth and last rung. Neptune used to be the body left out, on the grounds that the
 * Sun had to be eighth — but the game's own `planets` word pool teaches all eight by name, so a
 * ladder that skipped one was teaching two different solar systems.
 */
interface Body {
  name: string;
  color: number;
  /** Disc radius as a fraction of the slot size. */
  r: number;
  ring?: boolean;
  /** Second colour for surface markings (land, bands, storm). */
  detail?: number;
}

export const BODIES: Body[] = [
  { name: 'mercury', color: 0x9c8e82, r: 0.30 },
  { name: 'venus', color: 0xe0b055, r: 0.38, detail: 0xf2d79a },
  { name: 'earth', color: 0x3f7fd0, r: 0.40, detail: 0x4fae5a },
  { name: 'mars', color: 0xc35733, r: 0.35, detail: 0xe8dcd2 },
  { name: 'jupiter', color: 0xd2a271, r: 0.52, detail: 0xa9764c },
  { name: 'saturn', color: 0xe3c98a, r: 0.44, ring: true },
  { name: 'uranus', color: 0x8fd3dd, r: 0.42 },
  { name: 'neptune', color: 0x3f5cc4, r: 0.40, detail: 0x2a3f9e },
  { name: 'sun', color: 0xffcc33, r: 0.56, detail: 0xfff3b0 },
];

/**
 * The level at which a partial eclipse crosses the Sun — and ONLY that level, since an eclipse is
 * a passing event rather than a new resting state (see updateEclipse).
 *
 * Placed at 14 because that is exactly where the Sun stops changing: halos cap at 13 and flares at
 * 14, so from here on a player who keeps scoring would see nothing new. The eclipse is the next
 * beat, and a rarer, stranger one than "brighter again".
 *
 * Read from milestones.ts rather than written here, since the rung now also PAYS something — the
 * 🍝 Plate of the Recipe Kitchen (specs/recipe-mode.md §2.2) — and three readers of one number
 * must not each carry their own copy of it.
 */
const ECLIPSE_AT = ECLIPSE_STARS;

/**
 * The Sun's late game, past the point where more halo and more flares stop saying anything new.
 * Each step is a different KIND of change rather than more of the last one, so a player deep into
 * a run keeps being told something: it reddens, is eclipsed, shifts violet, then goes supernova.
 */
const SUN_ORANGE_AT = 13;
const SUN_VIOLET_AT = 15;
const SUPERNOVA_AT = 16;
/**
 * The supernova is an EVENT, not a state, so it is timed rather than simply drawn. The pause
 * before it is the important half: the star has to be seen intact first, or there is no explosion
 * — only a remnant that was always there. Then it comes apart slowly, over four seconds.
 */
const NOVA_DELAY_MS = 1500;
const NOVA_DURATION_MS = 4000;
/**
 * The last state, and the only one that REPLACES a body rather than dressing it: after the star
 * has blown up, pull back and the Sun's slot is a galaxy. Nothing follows it — a run this deep is
 * already far past anything the game expects.
 */
const GALAXY_AT = 17;

/** Unlit slots: dark enough to stay quiet, outlined so they read over any backdrop. */
const EMPTY_FILL = 0x3d444d;
const EMPTY_ALPHA = 0.55;
const OUTLINE = 0x1b2027;

/**
 * The waterline: a thin electric line at the fill level of the body in flight.
 *
 * Without it the column says nothing until the fill has climbed high enough to reach the disc —
 * at the start of a run, and again at the start of every new body, there is simply nothing there.
 * The line marks the level itself, so the very first correct answer visibly moves something.
 *
 * Redrawn on a timer rather than per frame: the jitter IS the effect, and 12 redraws a second
 * already reads as electric. Scene timers pause with the scene, so it also stops behind the Help
 * overlay like everything else on the HUD.
 */
const SPARK_TICK_MS = 80;
const SPARK_CORE = 0xdff4ff;
const SPARK_GLOW = 0x7ec4ff;

/**
 * Rendered stills of the bodies, keyed by rung and size (see PlanetStack.portrait). Module-level
 * rather than per scene: they are plain strings, so they outlive the scene that drew them and the
 * menu can be left and re-entered without rasterizing Saturn again.
 */
const PORTRAITS = new Map<string, string>();

export interface PlanetStackOptions {
  slots?: number;
  size?: number;
  spacing?: number;
}

export class PlanetStack extends Phaser.GameObjects.Container {
  private readonly lit: Phaser.GameObjects.Container[] = [];
  /** The unlit silhouettes, kept so the Sun's slot can be hidden outright at GALAXY_AT. */
  private readonly dims: Phaser.GameObjects.Image[] = [];
  private readonly masks: Phaser.GameObjects.Graphics[] = [];
  /** The geometry masks built from `masks`, kept so a full slot can drop its mask and take it back
   *  when it is the body in flight again (see setProgress). */
  private readonly geoMasks: Phaser.Display.Masks.GeometryMask[] = [];
  private readonly size: number;
  private readonly slotY: number[] = [];
  /** The Sun's halo, grown past the final slot. Null until the Sun itself is lit. */
  private sunGlow: Phaser.GameObjects.Graphics | null = null;
  private flares: Phaser.GameObjects.Graphics | null = null;
  /** The moon of the ECLIPSE_AT partial eclipse; null until the player gets there. */
  private eclipse: Phaser.GameObjects.Graphics | null = null;
  /** Colour wash over the Sun from SUN_ORANGE_AT onward. */
  private sunTint: Phaser.GameObjects.Graphics | null = null;
  /** The SUPERNOVA_AT blast: shells, filaments, fragments and the shock ring. */
  private nova: Phaser.GameObjects.Graphics | null = null;
  /** Explosion progress, 0 (star intact) to 1 (settled remnant). */
  private novaT = 0;
  private novaTween: Phaser.Tweens.Tween | null = null;
  /** The GALAXY_AT spiral, which stands in for the Sun entirely. */
  private galaxy: Phaser.GameObjects.Graphics | null = null;
  private earned = 0;
  /** The electric line at the fill level of the body in flight; null once the star has blown up. */
  private spark: Phaser.GameObjects.Graphics | null = null;
  private sparkTimer: Phaser.Time.TimerEvent | null = null;
  private sparkFraction = 0;
  /** Ticks with the redraw timer; seeds the jitter, so the line flickers instead of sitting still. */
  private sparkPhase = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: PlanetStackOptions = {}) {
    super(scene, x, y);
    scene.add.existing(this);

    const slots = opts.slots ?? BODIES.length;
    this.size = opts.size ?? 48;
    const spacing = opts.spacing ?? this.size + 8;

    // Built bottom-up: slot 0 sits at y=0 and later slots stack upward, so the column grows the
    // way a pile does rather than dropping downward from a fixed top.
    for (let i = 0; i < slots; i++) {
      const sy = -i * spacing;
      this.slotY.push(sy);
      const body = BODIES[Math.min(i, BODIES.length - 1)];

      const dim = scene.add.image(0, sy, PlanetStack.bodyTexture(scene, body, this.size, true));

      const litBody = scene.add.container(0, sy);
      litBody.add(scene.add.image(0, 0, PlanetStack.bodyTexture(scene, body, this.size, false)));

      // `make.graphics({}, false)` — NOT `add.graphics()`. A mask's Graphics must stay off the
      // display list: added, it renders as a white rectangle; hidden with setVisible(false) the
      // renderer skips it and never writes the stencil, so the mask silently does nothing.
      const mask = scene.make.graphics({}, false);
      const geoMask = mask.createGeometryMask();
      litBody.setMask(geoMask);
      this.geoMasks.push(geoMask);

      this.add([dim, litBody]);
      this.dims.push(dim);
      this.lit.push(litBody);
      this.masks.push(mask);
    }

    this.setProgress(0, 0);
  }

  /**
   * Half-width of the disc at height `y` — the chord. Surface markings are sized off this so a
   * band or a cap stays inside the limb; Graphics has no clipping, and a mask per body would be
   * eight more stencil passes for something this small.
   */
  private static chord(R: number, y: number): number {
    return Math.sqrt(Math.max(0, R * R - y * y));
  }

  /**
   * Strokes an elliptical arc as a polyline. Graphics.arc draws CIRCULAR arcs only, so the near
   * half of Saturn's ring — which has to match the tilted ellipse behind the planet — cannot be
   * drawn with it.
   */
  private static ellipseArc(
    g: Phaser.GameObjects.Graphics,
    cx: number, cy: number, rx: number, ry: number,
    a0: number, a1: number, steps = 24,
  ): void {
    g.beginPath();
    for (let i = 0; i <= steps; i++) {
      const a = a0 + ((a1 - a0) * i) / steps;
      const x = cx + Math.cos(a) * rx;
      const y = cy + Math.sin(a) * ry;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.strokePath();
  }

  /** A horizontal band between two heights, inset to the narrower of its two chords. */
  private static band(g: Phaser.GameObjects.Graphics, R: number, y0: number, y1: number): void {
    const w = PlanetStack.chord(R, Math.max(Math.abs(y0), Math.abs(y1)));
    g.fillRect(-w, y0, w * 2, y1 - y0);
  }

  /**
   * Sphere shading, and the reason these read as worlds rather than as coloured coins: a lit limb
   * on the upper left, a terminator falling away to the lower right, and a small specular
   * highlight. Faked with layered translucent circles, since Graphics has no gradients — offset
   * discs of dark and light approximate the cosine falloff closely enough at this size.
   */
  private static shade(g: Phaser.GameObjects.Graphics, R: number): void {
    // Terminator: dark discs pushed toward the lower right, each smaller and stronger.
    for (let i = 0; i < 3; i++) {
      const t = i / 3;
      g.fillStyle(0x05070c, 0.13 + 0.07 * i);
      g.fillCircle(R * (0.30 + 0.16 * t), R * (0.28 + 0.16 * t), R * (1.02 - 0.10 * i));
    }
    // Lit limb + specular highlight on the sunward side.
    g.fillStyle(0xffffff, 0.10);
    g.fillCircle(-R * 0.26, -R * 0.26, R * 0.72);
    g.fillStyle(0xffffff, 0.16);
    g.fillCircle(-R * 0.34, -R * 0.34, R * 0.30);
  }

  /**
   * A body, baked once into a texture and handed back as a texture key.
   *
   * Both states of every body are drawn in the constructor and never redrawn — the run's whole
   * choreography is `setVisible`, plus separate Graphics for the Sun's dressing (glow, flares,
   * nova). As retained Graphics they were still tessellated from scratch on every frame: Phaser 3
   * caches no Graphics geometry, and a body is a stack of circles and ellipses, each of which the
   * renderer walks at ~100 points per arc (`iterStep` 0.01 in GraphicsWebGLRenderer). Nine unlit
   * bodies measured 3.2 ms/frame on a desktop dev build — more than half the frame of the mode
   * every player plays, for a column of pictures that never change.
   *
   * Baked at 1:1, which is what they were rasterized at anyway (the WebGL canvas is a 1280x720
   * drawing buffer that the browser scales), so the pixels are the ones the vector path produced.
   * Keyed by body and size, so the Menu, the HUD and the Celebration screen share one set.
   */
  private static bodyTexture(scene: Phaser.Scene, body: Body, size: number, unlit: boolean): string {
    const key = `planet-${body.name}-${Math.round(size)}-${unlit ? 'dim' : 'lit'}`;
    if (scene.textures.exists(key)) return key;
    // Half-box: the disc plus its 2px limb, or the ring, which reaches 1.55R plus half its own
    // stroke. Generous by a hair rather than exact — a clipped ring would be a visible bug.
    const R = size * body.r;
    const half = Math.ceil(R * (body.ring ? 1.8 : 1.15)) + 3;
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    g.translateCanvas(half, half);   // drawBody works around (0, 0); the texture starts at its corner
    PlanetStack.drawBody(g, body, size, unlit);
    g.generateTexture(key, half * 2, half * 2);
    g.destroy();
    return key;
  }

  /** Draws one body into `g`, either as its lit self or as the unlit placeholder. */
  private static drawBody(
    g: Phaser.GameObjects.Graphics, body: Body, size: number, unlit: boolean,
  ): void {
    const R = size * body.r;

    if (body.ring) {
      // Back half of the ring, drawn before the disc so the planet occludes it. Tilted, with the
      // Cassini division as a second, thinner stroke — the gap is what makes a ring read as Saturn
      // rather than as a hoop.
      g.lineStyle(Math.max(2, R * 0.20), unlit ? EMPTY_FILL : 0xcdb078, unlit ? EMPTY_ALPHA : 0.95);
      g.strokeEllipse(0, -R * 0.10, R * 3.1, R * 0.94);
      if (!unlit) {
        g.lineStyle(Math.max(1, R * 0.05), 0x8d7648, 0.8);
        g.strokeEllipse(0, -R * 0.10, R * 2.55, R * 0.77);
      }
    }

    g.fillStyle(unlit ? EMPTY_FILL : body.color, unlit ? EMPTY_ALPHA : 1);
    g.fillCircle(0, 0, R);

    if (unlit) {
      g.lineStyle(2, OUTLINE, 0.7);
      g.strokeCircle(0, 0, R);
      return;
    }

    // ── surface, before the shading so the terminator falls across it ──────────────────────────
    const d = body.detail ?? body.color;
    switch (body.name) {
      case 'mercury': {
        // Cratered and colourless: a few darker basins and one bright ray crater.
        g.fillStyle(0x7d7167, 0.85);
        g.fillCircle(-R * 0.22, R * 0.18, R * 0.26);
        g.fillCircle(R * 0.30, -R * 0.14, R * 0.18);
        g.fillCircle(R * 0.06, R * 0.46, R * 0.14);
        g.fillStyle(0xb9ada2, 0.7).fillCircle(-R * 0.42, -R * 0.30, R * 0.13);
        break;
      }
      case 'venus': {
        // Featureless cloud deck — swirled bands, no surface.
        g.fillStyle(d, 0.55);
        PlanetStack.band(g, R, -R * 0.62, -R * 0.34);
        PlanetStack.band(g, R, -R * 0.06, R * 0.20);
        g.fillStyle(0xc9954a, 0.4);
        PlanetStack.band(g, R, R * 0.42, R * 0.66);
        break;
      }
      case 'earth': {
        // Continents, ice caps, and a wisp of cloud.
        g.fillStyle(d, 0.95);
        g.fillCircle(-R * 0.34, -R * 0.16, R * 0.32);
        g.fillCircle(-R * 0.10, R * 0.10, R * 0.20);
        g.fillCircle(R * 0.32, R * 0.26, R * 0.28);
        g.fillCircle(R * 0.40, -R * 0.34, R * 0.16);
        g.fillStyle(0xf2f7fb, 0.9);
        PlanetStack.band(g, R, -R, -R * 0.80);          // north cap
        PlanetStack.band(g, R, R * 0.82, R);            // south cap
        g.fillStyle(0xffffff, 0.35);
        g.fillCircle(R * 0.02, -R * 0.46, R * 0.22);
        break;
      }
      case 'mars': {
        // Dark maria and a bright northern cap.
        g.fillStyle(0x8f3a20, 0.75);
        g.fillCircle(R * 0.18, R * 0.12, R * 0.36);
        g.fillCircle(-R * 0.34, R * 0.30, R * 0.20);
        g.fillStyle(0xf4ece6, 0.95);
        PlanetStack.band(g, R, -R, -R * 0.74);
        break;
      }
      case 'jupiter': {
        // Alternating zones and belts, plus the Great Red Spot riding one of them.
        g.fillStyle(d, 0.85);
        PlanetStack.band(g, R, -R * 0.74, -R * 0.52);
        PlanetStack.band(g, R, -R * 0.24, -R * 0.04);
        PlanetStack.band(g, R, R * 0.30, R * 0.52);
        g.fillStyle(0xf0d3ae, 0.55);
        PlanetStack.band(g, R, -R * 0.48, -R * 0.28);
        PlanetStack.band(g, R, R * 0.04, R * 0.26);
        g.fillStyle(0xbf5334, 0.9);
        g.fillEllipse(R * 0.26, R * 0.40, R * 0.52, R * 0.30);
        break;
      }
      case 'saturn': {
        // Soft, low-contrast banding — Saturn's atmosphere is much blander than Jupiter's.
        g.fillStyle(0xcaa96b, 0.45);
        PlanetStack.band(g, R, -R * 0.58, -R * 0.36);
        PlanetStack.band(g, R, R * 0.10, R * 0.34);
        break;
      }
      case 'uranus': {
        // Nearly featureless; just a faint zonal hint so it is not a flat disc.
        g.fillStyle(0xb9e6ec, 0.35);
        PlanetStack.band(g, R, -R * 0.40, -R * 0.16);
        g.fillStyle(0x74bcc8, 0.3);
        PlanetStack.band(g, R, R * 0.26, R * 0.52);
        break;
      }
      case 'neptune': {
        // Uranus's near-twin, so it has to differ by more than hue at 40px: banded rather than
        // near-blank, plus the Great Dark Spot.
        //
        // The spot is deliberately soft and small. A first pass gave it hard contrast and a bright
        // companion cloud opposite, which is what Neptune actually looks like — but at this size
        // the two blobs stopped reading as weather and started reading as a scribbled numeral on
        // the disc. One low-contrast blemish is the most detail this many pixels can carry.
        g.fillStyle(d, 0.6);
        PlanetStack.band(g, R, -R * 0.66, -R * 0.40);
        PlanetStack.band(g, R, R * 0.18, R * 0.44);
        g.fillStyle(0x24337d, 0.6);
        g.fillEllipse(-R * 0.20, -R * 0.14, R * 0.44, R * 0.26);
        break;
      }
      case 'sun': {
        // Granulated photosphere, hot white core, cooler orange limb.
        g.fillStyle(0xff9c1f, 0.55);
        g.fillCircle(0, 0, R);
        g.fillStyle(d, 0.95);
        g.fillCircle(0, 0, R * 0.80);
        g.fillStyle(0xfffdf0, 0.85);
        g.fillCircle(-R * 0.10, -R * 0.10, R * 0.46);
        break;
      }
    }

    // The Sun is self-luminous, so it gets no terminator — everything else is lit from outside.
    if (body.name !== 'sun') PlanetStack.shade(g, R);

    if (body.ring) {
      // Near half of the ring, drawn OVER the disc (the far half is the full ellipse behind it),
      // plus the shadow the planet casts across it.
      const rx = R * 1.55;
      const ry = R * 0.47;
      const cy = -R * 0.10;
      g.lineStyle(Math.max(2, R * 0.20), 0xe0c489, 0.95);
      PlanetStack.ellipseArc(g, 0, cy, rx, ry, 0, Math.PI);
      g.lineStyle(Math.max(1, R * 0.05), 0x8d7648, 0.75);
      PlanetStack.ellipseArc(g, 0, cy, R * 1.28, R * 0.385, 0, Math.PI);   // Cassini division
      g.lineStyle(Math.max(2, R * 0.20), 0x2a2216, 0.40);
      PlanetStack.ellipseArc(g, 0, cy, rx, ry, Math.PI * 0.20, Math.PI * 0.52);
    }

    g.lineStyle(2, OUTLINE, 0.55);
    g.strokeCircle(0, 0, R);
  }

  /**
   * One rung of the ladder as a still picture, as a PNG data URL — for the Scoreboard's record
   * card, which shows the furthest body a lesson has reached (specs/scoreboard.md §2.1).
   *
   * That card is plain HTML in the overlay layer, so it cannot ask Phaser for anything itself; the
   * Menu hands it this instead. The point is that it is the SAME drawing the child just watched
   * fill the column — the card used to carry its own much simpler copy of each body, a disc with
   * one blob on it, and Mercury and Neptune were the same picture in two colours.
   *
   * `name` is a milestone name (src/game/milestones.ts): one of the nine bodies, or one of the
   * five late-Sun rungs. Null for anything else, and for a renderer that will not hand its pixels
   * back — the card keeps a plain drawn fallback for both.
   *
   * Cached by name and size: the readback rasterizes and base64s a bitmap, and the card can be
   * opened over and over from the menu.
   */
  static portrait(scene: Phaser.Scene, name: string, px: number): string | null {
    const key = `planet-portrait-${name}-${px}`;
    const cached = PORTRAITS.get(key);
    if (cached) return cached;

    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    g.translateCanvas(px / 2, px / 2);            // the poses are drawn around their own centre
    const drawn = PlanetStack.drawPortrait(g, name, px / 2);
    if (!drawn) {
      g.destroy();
      return null;
    }
    g.generateTexture(key, px, px);
    g.destroy();

    // generateTexture renders through the canvas path into a CanvasTexture, so its source image is
    // a canvas we can read back. Guarded rather than asserted: the data URL is a nicety, and the
    // card has something to show without it.
    const src = scene.textures.get(key).getSourceImage();
    const url = src instanceof HTMLCanvasElement ? src.toDataURL('image/png') : null;
    scene.textures.remove(key);                   // the URL is the product; nothing draws this
    if (url) PORTRAITS.set(key, url);
    return url;
  }

  /**
   * Poses one rung inside a box of half-width `half`, or returns false if the name is not one.
   *
   * Every radius here is a fraction of `half` rather than of the slot size, because a portrait has
   * a different job from a rung: on the column Mercury is small BESIDE Jupiter and that contrast is
   * the ladder, while on the card there is nothing beside it and a tiny disc in a big box just
   * looks like a mistake. So each body is drawn to fill its box, with the ones that wear something
   * — Saturn's ring, the Sun's halo, the remnant's shells — pulled in far enough to fit it.
   */
  private static drawPortrait(g: Phaser.GameObjects.Graphics, name: string, half: number): boolean {
    const body = BODIES.find((b) => b.name === name);
    if (body && body.name !== 'sun') {
      const R = half * (body.ring ? 0.56 : 0.62);     // the ring reaches 1.55R plus its own stroke
      PlanetStack.drawBody(g, body, R / body.r, false);
      return true;
    }

    switch (name) {
      case 'sun': PlanetStack.sunStill(g, half, BODIES.length); return true;
      case 'sun_red': PlanetStack.sunStill(g, half, SUN_ORANGE_AT); return true;
      case 'eclipse': PlanetStack.sunStill(g, half, ECLIPSE_AT); return true;
      case 'sun_violet': PlanetStack.sunStill(g, half, SUN_VIOLET_AT); return true;
      case 'supernova': {
        // The settled remnant (t = 1), which is the state that survives — and by then the star's
        // own disc is gone, exactly as on the column. Small R: the shells reach nearly 3R.
        const R = half * 0.30;
        PlanetStack.sunHalo(g, R, 3);
        PlanetStack.novaFrame(g, R, 1);
        return true;
      }
      case 'galaxy': {
        // Turned a little, unlike the column's, where the spin tween is doing that job: a spiral
        // seen from near its plane and left level is a flat bar across a square box.
        g.rotateCanvas(Phaser.Math.DegToRad(-18));
        PlanetStack.galaxyDisc(g, half * 0.82);       // the halo reaches 1.05 of this
        g.rotateCanvas(Phaser.Math.DegToRad(18));
        return true;
      }
      default: return false;
    }
  }

  /**
   * The Sun as it stands at `level` stars: halo, flares and the colour wash it has earned by then,
   * with the moon in front of it at ECLIPSE_AT.
   *
   * Three halo rings rather than the column's six. On the column the halo spills across the
   * neighbouring slots, which a box this size cannot do — and the ring count was never what tells
   * the late rungs apart anyway; the colour is.
   */
  private static sunStill(g: Phaser.GameObjects.Graphics, half: number, level: number): void {
    const sun = BODIES[BODIES.length - 1];
    const extra = level - BODIES.length;
    const R = half * 0.50;                          // the flares reach 1.8R, the halo 1.66R
    PlanetStack.sunHalo(g, R, Math.min(3, 2 + extra));
    if (extra > 0) PlanetStack.sunFlares(g, R, Math.min(12, (extra + 1) * 2), extra);
    PlanetStack.drawBody(g, sun, R / sun.r, false);
    if (level >= SUN_ORANGE_AT) PlanetStack.sunWash(g, R, level >= SUN_VIOLET_AT);
    if (level === ECLIPSE_AT) {
      // Held at the far end of the drift the column gives it (updateEclipse's tween target), which
      // is the deepest the moon ever gets — and still a PARTIAL eclipse, a crescent of Sun left
      // showing. Nearer the middle the disc is all but covered, and a still of that reads as the
      // reward switched off rather than as an eclipse.
      g.translateCanvas(R * 0.55, R * 0.10);
      PlanetStack.eclipseMoon(g, R);
      g.translateCanvas(-R * 0.55, -R * 0.10);
    }
  }

  /**
   * @param earned    whole slots lit
   * @param fraction  0..1 progress into the next one
   */
  setProgress(earned: number, fraction: number): void {
    this.earned = Math.max(0, Math.floor(earned));
    this.sparkFraction = Math.min(1, Math.max(0, fraction));
    for (let i = 0; i < this.lit.length; i++) {
      const filled = i < this.earned;
      const inFlight = i === this.earned;
      const f = filled ? 1 : inFlight ? this.sparkFraction : 0;
      this.lit[i].setVisible(f > 0);
      // A slot that is FULL needs no mask: its window covers the whole body, so the stencil pass
      // costs a clear, a mask draw and a restore to change nothing. Only the body in flight has a
      // fraction to show, so deep in a run this is one stencil pass instead of nine.
      if (f >= 1) {
        if (this.lit[i].mask) this.lit[i].clearMask();
        this.drawMask(i, 0);
      } else {
        if (!this.lit[i].mask) this.lit[i].setMask(this.geoMasks[i]);
        this.drawMask(i, f);
      }
    }
    this.updateSun();
    this.updateSpark();
  }

  /**
   * Height of the fill window for slot `i`: the body's OWN disc, not a fixed box.
   *
   * A single box for every slot was the first version, and it put the level line out in the empty
   * space between planets — most of Mercury's fraction was spent below its limb, and the last of
   * it above the limb with nothing left to fill. Measuring each body separately means the level
   * always runs from its lowest point to its highest, and every fraction of a body is disc.
   *
   * The 2px pad is the outline: `drawBody` strokes the limb with a 2px line, so a window of
   * exactly 2R would leave the top of that stroke unfilled.
   */
  private fillHeight(i: number): number {
    const body = BODIES[Math.min(i, BODIES.length - 1)];
    return this.size * body.r * 2 + 2;
  }

  /** Local y of the fill level within slot `i`, from its lowest point upward. */
  private fillY(i: number, fraction: number): number {
    const h = this.fillHeight(i);
    return h / 2 - h * fraction;
  }

  /** Exposes the bottom `fraction` of slot `i`, in WORLD coordinates (masks are not parented). */
  private drawMask(i: number, fraction: number): void {
    const g = this.masks[i];
    g.clear();
    if (fraction <= 0) return;
    const w = this.size * 3.2;              // wide enough to clear Saturn's ring
    const h = this.fillHeight(i);
    const cx = this.x;
    const cy = this.y + this.slotY[i];
    g.fillStyle(0xffffff);
    // Anchored to the bottom edge and grown upward, so a body fills like a glass.
    g.fillRect(cx - w / 2, cy + h * 0.5 - h * fraction, w, h * fraction);
  }

  /**
   * Keeps the waterline on the body in flight, creating it on first use and tearing it down once
   * there is no star left to fill.
   *
   * Past the last slot it stays on the Sun and climbs it again for every extra level. The halo,
   * flares and colour shifts only step on a WHOLE level, so without the line a player deep into a
   * run gets nothing at all for the answers in between — exactly the gap the line exists to close
   * lower down the column.
   *
   * It survives the supernova, where there is no disc left to fill and it reads as a bare level
   * line over the remnant. That is the lesser evil: the explosion is a one-shot few seconds at the
   * START of SUPERNOVA_AT, and the rest of that level would otherwise answer nothing. Only
   * GALAXY_AT ends it, where the slot stops being the Sun at all.
   */
  private updateSpark(): void {
    if (this.earned >= GALAXY_AT) {
      this.sparkTimer?.remove();
      this.spark?.destroy();
      this.sparkTimer = null;
      this.spark = null;
      return;
    }
    if (!this.spark) {
      this.spark = this.scene.add.graphics();
      this.add(this.spark);
      this.sparkTimer = this.scene.time.addEvent({
        delay: SPARK_TICK_MS,
        loop: true,
        callback: () => {
          this.sparkPhase++;
          this.drawSpark();
        },
      });
    }
    // Drawn above the silhouette, the lit disc AND the Sun's dressing, so the line stays readable
    // where it crosses them instead of disappearing into them. Re-asserted on every pass because
    // updateSun appends the tint and the eclipse moon after it.
    this.bringToTop(this.spark);
    this.drawSpark();
  }

  /**
   * One frame of the line: a jagged filament with a soft glow under it and an occasional spark,
   * as wide as the body it is filling and no wider. Deliberately faint — this is a level marker
   * riding under the planets, not a third thing competing with them for attention.
   */
  private drawSpark(): void {
    const g = this.spark;
    if (!g) return;
    // Clamped to the last slot: past the Sun there is no next body, so the line keeps riding the
    // Sun rather than walking off the top of the column.
    const slot = Math.min(this.earned, this.lit.length - 1);
    const body = BODIES[Math.min(slot, BODIES.length - 1)];
    const R = this.size * body.r;
    const level = this.fillY(slot, this.sparkFraction);
    const y = this.slotY[slot] + level;

    // Deterministic jitter seeded off the tick, like drawNova's: every point moves together on a
    // redraw and holds still between them, which is what makes it flicker rather than shimmer.
    const rnd = (i: number): number => {
      const x = Math.sin((i + this.sparkPhase * 17.3) * 91.7 + 47.3) * 43758.5453;
      return x - Math.floor(x);
    };

    // The line spans the disc at the height it has reached, so it hugs the limb — but never
    // narrower than 0.7 of it, or it would pinch to nothing exactly at the two moments it has to
    // be clearest: a body just started and a body just finished. At the limb that reads as an
    // underline the width of the planet, which is the point.
    const half = Math.max(R * 0.7, PlanetStack.chord(R, level));
    const amp = R * 0.16;
    const pts: Phaser.Math.Vector2[] = [];
    const steps = 10;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      // Pinned at both ends and loosest in the middle, so it reads as a line stretched between two
      // points rather than a torn edge.
      const slack = Math.sin(Math.PI * t);
      pts.push(new Phaser.Math.Vector2(-half + 2 * half * t, y + (rnd(i) - 0.5) * amp * slack));
    }

    // Weights come off the body's radius, not the slot: a stroke that suits the Sun is a bar
    // across Mercury.
    g.clear();
    g.lineStyle(Math.max(2.5, R * 0.22), SPARK_GLOW, 0.14);
    g.strokePoints(pts, false);
    g.lineStyle(Math.max(1.2, R * 0.07), SPARK_CORE, 0.6);
    g.strokePoints(pts, false);

    // A couple of sparks riding the filament — the difference between a lit wire and a live one.
    for (let i = 0; i < 2; i++) {
      const p = pts[1 + Math.floor(rnd(i + 40) * (pts.length - 2))];
      g.fillStyle(SPARK_CORE, 0.35 + 0.4 * rnd(i + 60));
      g.fillCircle(p.x, p.y, Math.max(1, R * (0.05 + 0.05 * rnd(i + 80))));
    }
  }

  /**
   * Past the last slot the run does not stop mattering: every further step brightens the Sun's
   * halo and adds a flare, so a player who keeps scoring keeps seeing an answer.
   *
   * Both ramps start one step in (2 halo rings, 4 flares) rather than from nothing. That drops
   * what used to be the ladder's weakest beat — the Sun wearing a bare two flares — and, since the
   * Sun moved from slot 8 to slot 9 when Neptune joined the stack, it also keeps every count
   * landing on the same absolute star level it always did.
   */
  private updateSun(): void {
    const extra = Math.max(0, this.earned - this.lit.length);
    if (this.earned < this.lit.length) {
      this.sunGlow?.destroy();
      this.flares?.destroy();
      this.eclipse?.destroy();
      this.sunTint?.destroy();
      this.novaTween?.remove();
      this.nova?.destroy();
      this.galaxy?.destroy();
      this.sunGlow = null;
      this.flares = null;
      this.eclipse = null;
      this.sunTint = null;
      this.novaTween = null;
      this.nova = null;
      this.novaT = 0;
      this.galaxy = null;
      this.dims[this.dims.length - 1]?.setVisible(true);
      return;
    }

    const sy = this.slotY[this.lit.length - 1];
    const R = this.size * BODIES[BODIES.length - 1].r;

    // The galaxy is resolved FIRST and short-circuits the rest. It tears the Sun's dressing down,
    // and everything below here rebuilds that dressing — leaving the flare block to run after it
    // put the rotating gold flares straight back around the galaxy.
    this.updateGalaxy(sy, R);
    if (this.galaxy) return;

    if (!this.sunGlow) {
      this.sunGlow = this.scene.add.graphics();
      this.addAt(this.sunGlow, 0);          // behind the disc
      this.scene.tweens.add({
        targets: this.sunGlow, alpha: { from: 0.75, to: 1 },
        duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    }
    // Each extra step adds a wider, brighter halo ring. The ramp starts two rings in, not one, so
    // the cap still lands on SUN_ORANGE_AT now that the Sun sits a slot higher (see the flares
    // below for the same offset, and ECLIPSE_AT for why the two caps matter).
    const halos = Math.min(6, 2 + extra);
    PlanetStack.sunHalo(this.sunGlow.clear().setPosition(0, sy), R, halos);

    this.updateSunTint(sy, R);
    this.updateEclipse(sy, R);
    this.updateNova(sy, R);
    // The eclipse is drawn last so its moon stays in front of the tint and the halo.
    if (this.eclipse) this.bringToTop(this.eclipse);

    if (extra <= 0) {
      this.flares?.destroy();
      this.flares = null;
      return;
    }

    // The flares are the living star's — they fade out as it comes apart and are never rebuilt,
    // which has to be checked BEFORE the create below or every pass would put them straight back.
    if (this.earned >= SUPERNOVA_AT) {
      this.fadeFlaresForNova();
      if (this.novaT >= 0.45) return;      // gone for good; do not rebuild
    }

    if (!this.flares) {
      this.flares = this.scene.add.graphics();
      this.addAt(this.flares, 0);
      this.scene.tweens.add({
        targets: this.flares, angle: 360, duration: 14000, repeat: -1, ease: 'Linear',
      });
    }
    // Two flares per extra step, evenly spaced around the disc — but the ramp starts one step in,
    // at FOUR. A Sun wearing exactly two flares reads as a mistake rather than a stage, a lone
    // pair of spikes on an otherwise round disc; four is the first count that looks deliberate.
    // The offset also keeps the cap on ECLIPSE_AT with the Sun a slot higher than it used to be.
    const count = Math.min(12, (extra + 1) * 2);
    PlanetStack.sunFlares(this.flares.clear().setPosition(0, sy), R, count, extra);
  }

  /** The Sun's halo: `halos` nested rings, each one wider and fainter than the last. */
  private static sunHalo(g: Phaser.GameObjects.Graphics, R: number, halos: number): void {
    for (let h = halos; h >= 1; h--) {
      g.fillStyle(0xffd23f, 0.10 + 0.05 * (halos - h));
      g.fillCircle(0, 0, R * (1 + 0.22 * h));
    }
  }

  /** `count` flares evenly around the disc, reaching further the deeper the run has gone. */
  private static sunFlares(
    g: Phaser.GameObjects.Graphics, R: number, count: number, extra: number,
  ): void {
    g.fillStyle(0xffe27a, 0.85);
    for (let i = 0; i < count; i++) {
      const a = (Math.PI * 2 * i) / count;
      const inner = R * 1.05;
      const outer = R * (1.5 + 0.05 * Math.min(6, extra + 1));
      const spread = 0.12;
      g.fillTriangle(
        Math.cos(a - spread) * inner, Math.sin(a - spread) * inner,
        Math.cos(a + spread) * inner, Math.sin(a + spread) * inner,
        Math.cos(a) * outer, Math.sin(a) * outer,
      );
    }
  }

  /**
   * The Sun reddens at SUN_ORANGE_AT and takes a violet cast at SUN_VIOLET_AT — a real star's
   * colour is its temperature, so a shifting colour is the most honest way to say "this is going
   * somewhere" before the supernova arrives.
   *
   * A wash over the disc rather than a redraw: the body's own Graphics is built once at
   * construction and sits behind a mask, so tinting it in place would mean rebuilding both.
   */
  private updateSunTint(sy: number, R: number): void {
    if (this.earned < SUN_ORANGE_AT || this.earned >= SUPERNOVA_AT) {
      this.sunTint?.destroy();
      this.sunTint = null;
      return;
    }
    if (!this.sunTint) {
      this.sunTint = this.scene.add.graphics();
      this.add(this.sunTint);
    }
    PlanetStack.sunWash(
      this.sunTint.clear().setPosition(0, sy), R, this.earned >= SUN_VIOLET_AT,
    );
  }

  /** The colour wash itself, so a still of the reddened or violet Sun can wear the same one. */
  private static sunWash(g: Phaser.GameObjects.Graphics, R: number, violet: boolean): void {
    // Orange first; the violet layers ON TOP of it rather than replacing it, so the star goes
    // through a plausible red-then-violet progression instead of switching palette.
    g.fillStyle(0xff6a12, 0.34);
    g.fillCircle(0, 0, R * 0.98);
    if (violet) {
      g.fillStyle(0x8b4dff, 0.30);
      g.fillCircle(0, 0, R * 0.98);
      g.fillStyle(0xd9b8ff, 0.22);
      g.fillCircle(-R * 0.20, -R * 0.20, R * 0.52);
    }
  }

  /**
   * SUPERNOVA_AT: the star lets go — drawn as a REMNANT rather than a starburst. The first pass
   * was eight symmetrical spikes, which reads as a cartoon sparkle; what these actually look like
   * is a ragged expanding cloud of shocked gas, threaded with filaments and bright knots, in the
   * teal (oxygen) and salmon (hydrogen) they photograph in, around the blue-hot collapsed core.
   *
   * The eclipse is torn down here — a moon in front of an exploding star reads as a bug, and the
   * supernova is the louder event.
   */
  private updateNova(sy: number, R: number): void {
    if (this.earned < SUPERNOVA_AT) {
      this.novaTween?.remove();
      this.nova?.destroy();
      this.novaTween = null;
      this.nova = null;
      this.novaT = 0;
      return;
    }
    this.eclipse?.destroy();
    this.eclipse = null;

    // Once the star has come apart its disc is gone — the remnant IS the object now. Re-asserted
    // on every pass because setProgress makes every earned slot visible again first.
    const sunSlot = this.lit.length - 1;
    this.lit[sunSlot]?.setVisible(this.novaT < 0.34);

    if (this.nova) return;               // the animation owns the drawing from here

    this.nova = this.scene.add.graphics();
    this.addAt(this.nova, 0);            // behind the disc, so the star is still there to explode
    this.drawNova(sy, R, 0);

    // A beat of nothing, then a slow explosion. The pause matters: the star has to be seen intact
    // before it goes, or there is no event — only a remnant that was always there.
    this.novaTween = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      delay: NOVA_DELAY_MS,
      duration: NOVA_DURATION_MS,
      ease: 'Cubic.easeOut',
      onUpdate: (tw) => {
        this.novaT = tw.getValue() ?? 0;
        if (this.nova) this.drawNova(sy, R, this.novaT);
        this.lit[sunSlot]?.setVisible(this.novaT < 0.34);
        // The flare fade has to be driven from HERE. updateSun only runs when the player scores,
        // so leaving it there meant the flares blazed on unchanged through the whole explosion
        // unless a correct answer happened to land inside those four seconds.
        this.fadeFlaresForNova();
      },
    });
  }

  /** Dims the living star's flares as it comes apart, and drops them once they are gone. */
  private fadeFlaresForNova(): void {
    if (!this.flares) return;
    const left = 1 - Math.min(1, Math.max(0, this.novaT / 0.45));
    if (left <= 0) {
      this.flares.destroy();
      this.flares = null;
      return;
    }
    this.flares.setAlpha(left);
  }

  /**
   * One frame of the explosion, `t` running 0 (intact) to 1 (settled remnant).
   *
   * The stages overlap deliberately — a real one does not step from flash to shell to ring, it
   * grows out of itself: the flash swells and dims, the shells expand and cool from white through
   * gold to red and teal, the knots ride outward as fragments, and the shock ring only becomes
   * visible once the front has left the core behind.
   */
  private drawNova(sy: number, R: number, t: number): void {
    PlanetStack.novaFrame(this.nova!.clear().setPosition(0, sy), R, t);
  }

  /** The frame itself, drawn into `g` around its own origin — see drawNova for what `t` means. */
  private static novaFrame(g: Phaser.GameObjects.Graphics, R: number, t: number): void {

    // Deterministic jitter. Math.random would re-roll the whole remnant on every frame of the
    // animation, and it would boil rather than expand.
    const rnd = (i: number): number => {
      const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
      return x - Math.floor(x);
    };
    const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
    // Fast at first, then easing out — the blast decelerates as it sweeps up the surrounding gas.
    const spread = Math.pow(t, 0.55);

    // 1. The flash: brilliant at the moment of collapse, gone by the time the shells are out.
    const flash = clamp01(1 - t / 0.34);
    if (flash > 0) {
      g.fillStyle(0xffffff, 0.9 * flash);
      g.fillCircle(0, 0, R * (0.5 + 2.6 * spread));
    }

    // 2. Shells of shocked gas, cooling outward as they expand.
    const lobe = (radius: number, wobble: number, color: number, alpha: number, seed: number): void => {
      if (radius <= 0 || alpha <= 0) return;
      const pts: Phaser.Math.Vector2[] = [];
      for (let i = 0; i < 22; i++) {
        const a = (Math.PI * 2 * i) / 22;
        const r = radius * (1 + wobble * (rnd(seed + i) - 0.5));
        pts.push(new Phaser.Math.Vector2(Math.cos(a) * r, Math.sin(a) * r * 0.88));
      }
      g.fillStyle(color, alpha);
      g.fillPoints(pts, true);
    };
    const shellA = clamp01((t - 0.05) / 0.35);
    lobe(R * 2.9 * spread, 0.42, 0x8f2f1c, 0.16 * shellA, 1);      // hydrogen, red
    lobe(R * 2.3 * spread, 0.38, 0x6d2c78, 0.16 * shellA, 40);     // violet mid-shell
    lobe(R * 1.8 * spread, 0.34, 0x1f6f86, 0.20 * shellA, 80);     // oxygen, teal

    // 3. Filaments, drawn out longer as the gas streams away from the core.
    for (let i = 0; i < 26; i++) {
      const a = (Math.PI * 2 * i) / 26 + (rnd(i + 200) - 0.5) * 0.22;
      const inner = R * (0.75 + 0.5 * rnd(i + 300)) * spread;
      const outer = inner + R * (0.7 + 1.7 * rnd(i + 400)) * spread;
      const wide = 0.018 + 0.03 * rnd(i + 500);
      g.fillStyle(i % 2 === 0 ? 0x7fe6dc : 0xff9b76, (0.28 + 0.32 * rnd(i + 600)) * shellA);
      g.fillTriangle(
        Math.cos(a - wide) * inner, Math.sin(a - wide) * inner * 0.9,
        Math.cos(a + wide) * inner, Math.sin(a + wide) * inner * 0.9,
        Math.cos(a) * outer, Math.sin(a) * outer * 0.9,
      );
    }

    // 4. Fragments: chunks of the star itself, tumbling outward. Rock rather than sparks — the
    //    gold flares that spun here belonged to the living star and fade out as these arrive, so
    //    the eye follows one thing being replaced by another instead of both at once.
    const ROCK = [0x9aa3ad, 0x6f9bd8, 0x9b7fd4];   // grey, blue, violet
    for (let i = 0; i < 15; i++) {
      const a = Math.PI * 2 * rnd(i + 700);
      const r = R * (1.0 + 1.5 * rnd(i + 800)) * spread;
      const cx = Math.cos(a) * r;
      const cy = Math.sin(a) * r * 0.9;
      // Sized off the disc, not off nothing: at 0.09R these were 2px specks and simply vanished.
      const size = R * (0.17 + 0.20 * rnd(i + 1000));
      // Each tumbles at its own rate and direction, carried by the same t that throws it outward.
      const spin = t * (2.2 + 5.5 * rnd(i + 1100)) * (rnd(i + 1200) > 0.5 ? 1 : -1);

      const pts: Phaser.Math.Vector2[] = [];
      const corners = 5 + Math.floor(rnd(i + 1300) * 3);
      for (let c = 0; c < corners; c++) {
        const ca = spin + (Math.PI * 2 * c) / corners;
        const cr = size * (0.62 + 0.55 * rnd(i * 31 + c + 1400));
        pts.push(new Phaser.Math.Vector2(cx + Math.cos(ca) * cr, cy + Math.sin(ca) * cr));
      }
      g.fillStyle(ROCK[i % ROCK.length], (0.55 + 0.35 * rnd(i + 950)) * shellA);
      g.fillPoints(pts, true);
      // A lit edge on the core-facing side, so they read as solid bodies rather than flat holes.
      g.lineStyle(Math.max(1, R * 0.03), 0xe8f2ff, 0.45 * shellA);
      g.strokePoints(pts, true);
    }

    // 5. The shock ring, which only reads once the front is clear of the core.
    const ringA = clamp01((t - 0.45) / 0.4);
    if (ringA > 0) {
      const rr = R * 2.55 * spread;
      g.lineStyle(Math.max(1.5, R * 0.10), 0xbff3ff, 0.55 * ringA);
      PlanetStack.ellipseArc(g, 0, 0, rr, rr * 0.88, 0, Math.PI * 2, 40);
      g.lineStyle(Math.max(1, R * 0.05), 0xffd9c2, 0.40 * ringA);
      PlanetStack.ellipseArc(g, 0, 0, rr * 0.82, rr * 0.72, 0, Math.PI * 2, 40);
    }

    // 6. What is left: the blue-hot collapsed core.
    const coreA = clamp01((t - 0.15) / 0.3);
    g.fillStyle(0x7fc9ff, 0.35 * coreA);
    g.fillCircle(0, 0, R * 0.95);
    g.fillStyle(0xdff0ff, 0.75 * coreA);
    g.fillCircle(0, 0, R * 0.52);
    g.fillStyle(0xffffff, 0.98 * coreA);
    g.fillCircle(0, 0, R * 0.26);
  }
  /**
   * GALAXY_AT: the widest shot the column can take. The star has already blown up, so the slot
   * stops being a body at all — the Sun's disc and its silhouette are hidden and a barred spiral
   * turns slowly in their place, with everything that dressed the Sun torn down.
   *
   * Sized to ~1.4x the Sun's radius on purpose: the column sits 40px from the canvas edge, and
   * anything wider would be clipped by it.
   */
  private updateGalaxy(sy: number, R: number): void {
    const sunSlot = this.lit.length - 1;
    if (this.earned < GALAXY_AT) {
      this.galaxy?.destroy();
      this.galaxy = null;
      this.dims[sunSlot]?.setVisible(true);
      return;
    }

    // A galaxy replaces the star; none of the Sun's dressing survives the pull-back — including
    // the explosion's own tween, which would otherwise keep poking the hidden Sun slot.
    this.novaTween?.remove();
    this.novaTween = null;
    this.nova?.destroy();
    this.sunTint?.destroy();
    this.sunGlow?.destroy();
    this.flares?.destroy();
    this.nova = null;
    this.sunTint = null;
    this.sunGlow = null;
    this.flares = null;
    this.dims[sunSlot]?.setVisible(false);
    this.lit[sunSlot]?.setVisible(false);

    if (this.galaxy) return;             // already drawn; the spin tween owns it from here

    const g = this.scene.add.graphics();
    PlanetStack.galaxyDisc(g, R * 1.4);

    g.setPosition(0, sy);
    this.add(g);
    this.galaxy = g;

    this.scene.tweens.add({
      targets: g, angle: 360, duration: 42000, repeat: -1, ease: 'Linear',
    });
  }

  /** The spiral itself, out to `MAX` — the arms' reach, and 1.05 MAX for the halo around them. */
  private static galaxyDisc(g: Phaser.GameObjects.Graphics, MAX: number): void {
    const TILT = 0.42;                   // seen from near its plane, not face-on

    // Diffuse halo, then the two arms, then the core on top.
    g.fillStyle(0x6a4fb0, 0.16);
    g.fillEllipse(0, 0, MAX * 2.1, MAX * 2.1 * TILT);
    g.fillStyle(0x8f6fd6, 0.14);
    g.fillEllipse(0, 0, MAX * 1.5, MAX * 1.5 * TILT);

    // Two logarithmic spirals, sampled as dots that thin and cool toward the rim.
    for (let arm = 0; arm < 2; arm++) {
      const phase = Math.PI * arm;
      for (let i = 0; i < 46; i++) {
        const t = i / 45;
        const theta = phase + t * Math.PI * 2.6;
        // Logarithmic spiral r = a*e^(b*t); b chosen so the arm just reaches MAX at t=1.
        const r = Math.min(MAX, MAX * 0.16 * Math.exp(1.82 * t));
        const x = Math.cos(theta) * r;
        const y = Math.sin(theta) * r * TILT;
        // Hot blue-white young stars outward, warmer dust inward.
        g.fillStyle(t > 0.55 ? 0xbcd8ff : 0xffe6b8, 0.85 - 0.45 * t);
        g.fillCircle(x, y, Math.max(0.8, MAX * (0.075 - 0.045 * t)));
      }
    }

    g.fillStyle(0xfff3cf, 0.55);
    g.fillEllipse(0, 0, MAX * 0.72, MAX * 0.72 * TILT + MAX * 0.10);
    g.fillStyle(0xfffdf4, 0.95);
    g.fillEllipse(0, 0, MAX * 0.34, MAX * 0.34 * TILT + MAX * 0.07);
  }

  /**
   * A partial eclipse at ECLIPSE_AT and ONLY there: a dark disc drifting across the Sun, never
   * quite covering it. Drawn on top of everything, and outside the slot's mask, so it is
   * unaffected by the fill — by this point the Sun is long since complete.
   *
   * It is a passing event, not a new resting state. Leaving it up would have the moon still
   * sitting in front of the star while the violet cast arrives at 15, muddying both.
   */
  private updateEclipse(sy: number, R: number): void {
    if (this.earned !== ECLIPSE_AT) {
      this.eclipse?.destroy();
      this.eclipse = null;
      return;
    }
    if (this.eclipse) return;             // already running; the drift tween owns it from here

    const moon = this.scene.add.graphics();
    PlanetStack.eclipseMoon(moon, R);
    moon.setPosition(-R * 1.5, sy - R * 0.30);
    this.add(moon);                        // last child = drawn above the Sun
    this.eclipse = moon;

    // Slow drift across the disc and back. It stops well short of totality: a partial eclipse
    // keeps the Sun visible, and a fully covered Sun would just read as the reward switching off.
    this.scene.tweens.add({
      targets: moon,
      x: R * 0.55,
      y: sy + R * 0.10,
      duration: 9000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** The moon that crosses the Sun: a dark disc with a sliver of corona along its edge. */
  private static eclipseMoon(g: Phaser.GameObjects.Graphics, R: number): void {
    g.fillStyle(0x161b21, 0.96);
    g.lineStyle(2, 0xffe27a, 0.55);
    g.fillCircle(0, 0, R * 0.92);
    g.strokeCircle(0, 0, R * 0.92);
  }

  /**
   * A UFO settling over one body, then drifting off as it fades — how the game says "that planet
   * earned you a Survival run" (UFO_AT_STACK_STARS) without taking the screen away from a child
   * who is mid-board.
   *
   * Drawn like the menu's UFO button (index.html `.menu-ufo`): same 👽, same saucer colours. The
   * prize and the thing you tap to spend it have to read as one object, or the reward means
   * nothing when it turns up on the menu later.
   *
   * Scene-level and sized off `this.size`, like popStar's sparks — it must survive the redraws
   * setProgress does to the slots underneath it.
   */
  flashUfo(index: number): void {
    const i = Phaser.Math.Clamp(index, 0, this.slotY.length - 1);
    const scene = this.scene;
    const u = this.size * 0.5;

    const saucer = scene.add.graphics();
    saucer.fillStyle(0x7ec4ff, 0.25).fillEllipse(0, 0, u * 3.2, u * 1.3);
    saucer.fillStyle(0x4a6480).fillEllipse(0, u * 0.1, u * 2.3, u * 0.72);
    saucer.fillStyle(0x8fb6d8).fillEllipse(0, -u * 0.08, u * 2.2, u * 0.54);
    saucer.fillStyle(0xcfe9ff).fillEllipse(0, -u * 0.22, u * 1.3, u * 0.26);
    saucer.lineStyle(2, 0xaee0ff, 1).strokeEllipse(0, u * 0.1, u * 2.3, u * 0.72);

    const alien = scene.add.text(0, -u * 0.78, '👽', {
      fontFamily: FONT_STACK, fontSize: `${Math.round(u * 1.5)}px`, align: 'center',
    }).setOrigin(0.5);

    const badge = scene.add.container(this.x, this.y + this.slotY[i] - this.size * 0.2, [saucer, alien])
      .setDepth(this.depth + 2)
      .setAlpha(0)
      .setScale(0.6);

    // Arrives quickly, then has two full seconds to leave: the fade IS the message that this was
    // a moment, not a new permanent thing on the HUD.
    scene.tweens.add({
      targets: badge,
      alpha: 1,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: badge,
          alpha: 0,
          y: badge.y - this.size * 0.6,
          duration: 2000,
          ease: 'Sine.easeIn',
          onComplete: () => badge.destroy(),
        });
      },
    });
  }

  /**
   * A 💎 settling over one body, then drifting off as it fades — how the game says "that planet
   * earned you a Pairs run" (DIAMOND_AT_STACK_STARS = Saturn) without taking the screen away from
   * a child who is mid-board. Same choreography, timing and scene-level lifetime as flashUfo; the
   * two treats differ in what they show and what they sound like, not in how they arrive.
   */
  flashDiamond(index: number): void {
    const i = Phaser.Math.Clamp(index, 0, this.slotY.length - 1);
    const scene = this.scene;
    const u = this.size * 0.5;

    // A glow plate behind the gem: the emoji alone disappears against Saturn's own gold.
    const glow = scene.add.graphics();
    glow.fillStyle(0x9be9ff, 0.22).fillCircle(0, 0, u * 1.5);
    glow.fillStyle(0xdff6ff, 0.16).fillCircle(0, 0, u * 1.0);

    const gem = scene.add.text(0, 0, '💎', {
      fontFamily: FONT_STACK, fontSize: `${Math.round(u * 1.7)}px`, align: 'center',
    }).setOrigin(0.5);

    const badge = scene.add.container(this.x, this.y + this.slotY[i] - this.size * 0.2, [glow, gem])
      .setDepth(this.depth + 2)
      .setAlpha(0)
      .setScale(0.6);

    scene.tweens.add({
      targets: badge,
      alpha: 1,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: badge,
          alpha: 0,
          y: badge.y - this.size * 0.6,
          duration: 2000,
          ease: 'Sine.easeIn',
          onComplete: () => badge.destroy(),
        });
      },
    });
  }

  /**
   * A 🐱 settling over one body — how the game says "that planet earned you a Cat & Mouse run"
   * (CAT_AT_STACK_STARS = Venus, the LOWEST rung of the ladder). Same choreography, timing and
   * scene-level lifetime as flashUfo/flashDiamond/flashCube; the treats differ in what they show
   * and what they sound like, not in how they arrive.
   *
   * Warm cream rather than frost or gold: Venus is the pale body, and a cat laid straight on it
   * without a plate behind is a smudge at this size.
   */
  flashCat(index: number): void {
    const i = Phaser.Math.Clamp(index, 0, this.slotY.length - 1);
    const scene = this.scene;
    const u = this.size * 0.5;

    const glow = scene.add.graphics();
    glow.fillStyle(0xffe8bf, 0.26).fillCircle(0, 0, u * 1.5);
    glow.fillStyle(0xfff6e4, 0.18).fillCircle(0, 0, u * 1.0);

    const cat = scene.add.text(0, 0, '🐱', {
      fontFamily: FONT_STACK, fontSize: `${Math.round(u * 1.7)}px`, align: 'center',
    }).setOrigin(0.5);

    const badge = scene.add.container(this.x, this.y + this.slotY[i] - this.size * 0.2, [glow, cat])
      .setDepth(this.depth + 2)
      .setAlpha(0)
      .setScale(0.6);

    scene.tweens.add({
      targets: badge,
      alpha: 1,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: badge,
          alpha: 0,
          y: badge.y - this.size * 0.6,
          duration: 2000,
          ease: 'Sine.easeIn',
          onComplete: () => badge.destroy(),
        });
      },
    });
  }

  /**
   * A 🚀 settling over one body — "that rung earned you a Rocket Read run" (ROCKET_AT_STACK_STARS =
   * star 15, where the Sun turns violet). The Worm's choreography; a warm plate, because a rocket
   * on a violet Sun with a violet glow behind it disappears into it.
   */
  flashRocket(index: number): void {
    this.flashGlyph(index, '🚀', 0xffe2b8, 0xfff4e6);
  }

  /**
   * A 🪱 settling over one body — how the game says "that rung earned you a Space Worm run"
   * (WORM_AT_STACK_STARS = star 13, where the Sun reddens). Same choreography and lifetime as
   * flashCat/flashUfo; the treats differ in what they show, not in how they arrive.
   *
   * Violet-white rather than the Cat's cream: this treat belongs to the dark sky, and a worm laid
   * on a red Sun without a cool plate behind it disappears into it.
   */
  flashWorm(index: number): void {
    this.flashGlyph(index, '🪱', 0xcfd6ff, 0xf0f2ff);
  }

  private flashGlyph(index: number, glyph: string, outer: number, inner: number): void {
    const i = Phaser.Math.Clamp(index, 0, this.slotY.length - 1);
    const scene = this.scene;
    const u = this.size * 0.5;

    const glow = scene.add.graphics();
    glow.fillStyle(outer, 0.28).fillCircle(0, 0, u * 1.5);
    glow.fillStyle(inner, 0.18).fillCircle(0, 0, u * 1.0);

    const face = scene.add.text(0, 0, glyph, {
      fontFamily: FONT_STACK, fontSize: `${Math.round(u * 1.7)}px`, align: 'center',
    }).setOrigin(0.5);

    const badge = scene.add.container(this.x, this.y + this.slotY[i] - this.size * 0.2, [glow, face])
      .setDepth(this.depth + 2)
      .setAlpha(0)
      .setScale(0.6);

    scene.tweens.add({
      targets: badge,
      alpha: 1,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: badge,
          alpha: 0,
          y: badge.y - this.size * 0.6,
          duration: 2000,
          ease: 'Sine.easeIn',
          onComplete: () => badge.destroy(),
        });
      },
    });
  }


  /**
   * A 🧊 settling over one body, then drifting off as it fades — how the game says "that planet
   * earned you an Ice Maze run" (ICE_AT_STACK_STARS = Neptune). Same choreography, timing and
   * scene-level lifetime as flashUfo/flashDiamond; the three treats differ in what they show and
   * what they sound like, not in how they arrive.
   */
  flashCube(index: number): void {
    const i = Phaser.Math.Clamp(index, 0, this.slotY.length - 1);
    const scene = this.scene;
    const u = this.size * 0.5;

    // A frost plate behind the cube: Neptune is itself deep blue, and the emoji alone sinks into it.
    const glow = scene.add.graphics();
    glow.fillStyle(0xbfefff, 0.24).fillCircle(0, 0, u * 1.5);
    glow.fillStyle(0xffffff, 0.16).fillCircle(0, 0, u * 1.0);

    const cube = scene.add.text(0, 0, '🧊', {
      fontFamily: FONT_STACK, fontSize: `${Math.round(u * 1.7)}px`, align: 'center',
    }).setOrigin(0.5);

    const badge = scene.add.container(this.x, this.y + this.slotY[i] - this.size * 0.2, [glow, cube])
      .setDepth(this.depth + 2)
      .setAlpha(0)
      .setScale(0.6);

    scene.tweens.add({
      targets: badge,
      alpha: 1,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: badge,
          alpha: 0,
          y: badge.y - this.size * 0.6,
          duration: 2000,
          ease: 'Sine.easeIn',
          onComplete: () => badge.destroy(),
        });
      },
    });
  }

  /**
   * A 🛰️ over the Sun's slot — the Word Labyrinth treat (specs/labyrinth-mode.md §2.3). Same
   * choreography, timing and scene-level lifetime as flashUfo/flashDiamond/flashCube.
   *
   * Gold rather than frost: this treat is bought by filling the solar system, so it belongs to the
   * Sun the way the Ice Cube belongs to Neptune. The plate matters more here than on the other
   * three — the Sun's slot is already the brightest thing on the stack, and a satellite laid
   * straight on it disappears.
   */
  flashSatellite(index: number): void {
    const i = Phaser.Math.Clamp(index, 0, this.slotY.length - 1);
    const scene = this.scene;
    const u = this.size * 0.5;

    const glow = scene.add.graphics();
    glow.fillStyle(0x1a2430, 0.42).fillCircle(0, 0, u * 1.55);
    glow.fillStyle(0xffd166, 0.26).fillCircle(0, 0, u * 1.15);

    const sat = scene.add.text(0, 0, '🛰️', {
      fontFamily: FONT_STACK, fontSize: `${Math.round(u * 1.7)}px`, align: 'center',
    }).setOrigin(0.5);

    const badge = scene.add.container(this.x, this.y + this.slotY[i] - this.size * 0.2, [glow, sat])
      .setDepth(this.depth + 2)
      .setAlpha(0)
      .setScale(0.6);

    scene.tweens.add({
      targets: badge,
      alpha: 1,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: badge,
          alpha: 0,
          y: badge.y - this.size * 0.6,
          duration: 2000,
          ease: 'Sine.easeIn',
          onComplete: () => badge.destroy(),
        });
      },
    });
  }

  /**
   * A 🍝 over the Sun's slot as the moon crosses it — the Recipe Kitchen treat
   * (specs/recipe-mode.md §2.3). Same choreography, timing and scene-level lifetime as
   * flashUfo/flashDiamond/flashCube/flashSatellite.
   *
   * The plate behind it is warm white rather than the Satellite's gold: this fires DURING the
   * eclipse, when the slot is a bright disc with a dark moon over it, and a gold badge on gold
   * reads as part of the Sun. White is the one value neither half of that rung uses.
   */
  flashPasta(index: number): void {
    const i = Phaser.Math.Clamp(index, 0, this.slotY.length - 1);
    const scene = this.scene;
    const u = this.size * 0.5;

    const glow = scene.add.graphics();
    glow.fillStyle(0x1a2430, 0.46).fillCircle(0, 0, u * 1.55);
    glow.fillStyle(0xfff3e0, 0.30).fillCircle(0, 0, u * 1.15);

    const plate = scene.add.text(0, 0, '🍝', {
      fontFamily: FONT_STACK, fontSize: `${Math.round(u * 1.7)}px`, align: 'center',
    }).setOrigin(0.5);

    const badge = scene.add.container(this.x, this.y + this.slotY[i] - this.size * 0.2, [glow, plate])
      .setDepth(this.depth + 2)
      .setAlpha(0)
      .setScale(0.6);

    scene.tweens.add({
      targets: badge,
      alpha: 1,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: badge,
          alpha: 0,
          y: badge.y - this.size * 0.6,
          duration: 2000,
          ease: 'Sine.easeIn',
          onComplete: () => badge.destroy(),
        });
      },
    });
  }

  /**
   * A 🌌 lifting out of the galaxy itself, then drifting off as it fades — how the game says "the
   * top of the ladder earned you a Word Wagon run" (GALAXY_AT_STACK_STARS, specs/wagon-mode.md
   * §2.3). Same choreography, timing and scene-level lifetime as flashUfo/flashDiamond/flashCube.
   *
   * The one that has no planet of its own: at this star the Sun's slot stops being a body and
   * becomes the spiral (updateGalaxy), so the badge lands on the last slot and the thing it lands
   * on IS the treat. It is deliberately a **scene-level** container like its three siblings rather
   * than a child of this stack — the galaxy takeover tears every piece of the Sun's dressing down
   * and short-circuits the slot's drawing, so anything parented here on the frame the spiral
   * arrives would go with it, and this treat's flourish only ever plays on the one run in a
   * hundred that gets this far (HUDScene holds it a second, by which time the pull-back is done).
   */
  flashGalaxy(): void {
    const i = this.slotY.length - 1;               // the Sun's slot — the spiral, by now
    const scene = this.scene;
    const u = this.size * 0.5;

    // A violet plate behind the glyph: the spiral it is landing on is itself bright at the core.
    const glow = scene.add.graphics();
    glow.fillStyle(0x8b4dff, 0.26).fillCircle(0, 0, u * 1.6);
    glow.fillStyle(0xd9c2f2, 0.18).fillCircle(0, 0, u * 1.0);

    const spiral = scene.add.text(0, 0, '🌌', {
      fontFamily: FONT_STACK, fontSize: `${Math.round(u * 1.7)}px`, align: 'center',
    }).setOrigin(0.5);

    const badge = scene.add.container(this.x, this.y + this.slotY[i] - this.size * 0.2, [glow, spiral])
      .setDepth(this.depth + 2)
      .setAlpha(0)
      .setScale(0.6);

    scene.tweens.add({
      targets: badge,
      alpha: 1,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: badge,
          alpha: 0,
          y: badge.y - this.size * 0.6,
          duration: 2000,
          ease: 'Sine.easeIn',
          onComplete: () => badge.destroy(),
        });
      },
    });
  }

  /** The celebratory pop for a body that just completed: a scale kick plus a ring of sparks. */
  popStar(index: number): void {
    const slot = this.lit[Math.min(index, this.lit.length - 1)];
    if (!slot) return;
    const scene = this.scene;

    // Once the galaxy has taken over, the Sun's gold sparks are a leftover — they belonged to a
    // star that is no longer there, and the slot they burst around is hidden. The galaxy gets a
    // slow breath instead, which is all a level past 17 needs.
    if (this.galaxy) {
      scene.tweens.add({
        targets: this.galaxy, scale: 1.12, duration: 320, yoyo: true, ease: 'Sine.easeOut',
      });
      return;
    }

    scene.tweens.add({
      targets: slot, scale: 1.5, duration: 170, yoyo: true, ease: 'Quad.easeOut',
    });

    const wx = this.x;
    const wy = this.y + this.slotY[Math.min(index, this.slotY.length - 1)];
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      const spark = scene.add.circle(wx, wy, Math.max(3, this.size * 0.07), 0xffe27a)
        .setDepth(this.depth + 1);
      scene.tweens.add({
        targets: spark,
        x: wx + Math.cos(angle) * 56,
        y: wy + Math.sin(angle) * 56,
        alpha: 0,
        scale: 0.3,
        duration: 520,
        ease: 'Cubic.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
  }

  destroy(fromScene?: boolean): void {
    // The timer is the scene's, not a child of this container, so it outlives the stack unless it
    // is removed here — and it would go on poking a destroyed Graphics every 80ms.
    this.sparkTimer?.remove();
    this.sparkTimer = null;
    for (const g of this.masks) g.destroy();
    super.destroy(fromScene);
  }
}
